import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  combineLatest,
  map,
  skip,
  Subscription,
  tap,
  withLatestFrom,
} from 'rxjs';

import { CollectionPackagingUnitHierarchyDto } from '@app/api/models';
import {
  ProductActions,
  ProductSelectors,
} from '@products/store/products.slice';

import {
  ProductCollectionsActions,
  ProductCollectionsSelectors,
} from './store/product-collections.slice';

@Component({
  selector: 'app-product-collections',
  templateUrl: './product-collections.component.html',
  styleUrls: ['./product-collections.component.scss'],
})
export class ProductCollectionsComponent implements OnInit, OnDestroy {
  packagingUnitId!: string;
  form = this.fb.group({
    collections: this.fb.array([]),
  });
  readOnly = false;

  subs = new Subscription();

  openedGroupIndexes: Array<number> = [];
  editingGroupIndex: number | null = null;
  editingLevelIndex: number | null = null;

  showLevelButtons(groupIndex: number, levelIndex: number): boolean {
    if (groupIndex !== this.editingGroupIndex) {
      return true;
    } else {
      return levelIndex === this.editingLevelIndex;
    }
  }

  originalValue = {
    id: '',
    withDepositFee: false,
    packingMethodSubType: '',
    refId: '',
    amount: 0,
    unit: 'PIECES',
    statementId: '',
    unique: false,
  };

  get collections() {
    return this.form.controls['collections'] as FormArray;
  }

  collectionLevels(groupIndex: number): FormArray {
    return (this.collections.at(groupIndex) as UntypedFormGroup).controls[
      'collectionLevels'
    ] as FormArray;
  }

  collectionsLoading$ = this.store.select(
    ProductCollectionsSelectors.selectCollectionsLoading
  );
  collections$ = this.store
    .select(ProductCollectionsSelectors.selectFlatCollectionList)
    .pipe(
      tap((collections) => {
        this.loadPackagingUnitsBySubtype(
          collections.map((collection) => ({
            withDepositFee: !!collection.withDepositFee,
            packingMethodSubType: collection.packingMethodSubType,
          }))
        );
      }),
      map((collections) =>
        Object.values(
          collections.reduce(
            (
              groups: {
                [key: number]: Array<
                  CollectionPackagingUnitHierarchyDto & {
                    groupIndex: number;
                  }
                >;
              },
              collectionLevel
            ) => {
              groups[collectionLevel.groupIndex] =
                groups[collectionLevel.groupIndex] ?? [];
              groups[collectionLevel.groupIndex].push(collectionLevel);
              return groups;
            },
            {}
          )
        )
      )
    );

  constructor(
    private fb: FormBuilder,
    private readonly store: Store,
    readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.subs.add(
      combineLatest([
        this.store.select(ProductSelectors.selectEditedProductPackageUnitId),
        this.route.queryParams,
        this.route.parent!.data,
      ]).subscribe(([packagingUnitId, params, parentData]) => {
        this.readOnly = parentData['readOnly'];
        if (!packagingUnitId) {
          this.store.dispatch(
            ProductActions.loadEditedProduct.trigger({
              packagingUnitId: params['id'],
              editable: !this.readOnly,
            })
          );
        } else {
          this.packagingUnitId = packagingUnitId;
        }
      })
    );

    this.subs.add(
      this.store
        .select(ProductCollectionsSelectors.selectRemoveCreatedUniqueGroupIndex)
        .subscribe((groupIndex) => {
          if (groupIndex > -1) {
            if (this.collectionLevels(groupIndex).length > 1) {
              this.collectionLevels(groupIndex).removeAt(
                this.collectionLevels(groupIndex).length - 1
              );
              this.editingGroupIndex = null;
              this.editingLevelIndex = null;
            } else {
              if (this.collections.length > 1) {
                this.collections.removeAt(this.collections.length - 1);
                this.editingGroupIndex = null;
                this.editingLevelIndex = null;
              } else {
                this.collections.clear();
                this.addCollection();
                window.scrollTo({ top: 0 });
              }
            }
          }
        })
    );

    this.subs.add(
      this.store
        .select(ProductSelectors.selectEditedProductPackageUnitId)
        .subscribe((packagingUnitId) => {
          if (packagingUnitId) {
            this.store.dispatch(
              ProductCollectionsActions.loadCollections.trigger({
                packagingUnitId,
              })
            );
          }
        })
    );

    this.subs.add(
      this.collections$
        .pipe(
          skip(1),
          withLatestFrom(
            this.store.select(ProductCollectionsSelectors.selectScrollTop)
          )
        )
        .subscribe(([collectionGroups, scrollTop]) => {
          this.collections.clear();
          this.editingGroupIndex = null;
          this.editingLevelIndex = null;
          if (collectionGroups.length) {
            this.buildCollections(collectionGroups);
          } else if (!this.readOnly) {
            this.addCollection();
          }
          setTimeout(() => {
            window.scrollTo({ top: scrollTop });
          });
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.store.dispatch(ProductCollectionsActions.reset());
  }

  getCollectorLevel(groupIndex: number, index: number): UntypedFormGroup {
    return this.collectionLevels(groupIndex).controls[
      index
    ] as UntypedFormGroup;
  }

  loadPackagingUnitsBySubtype(
    subTypes: Array<{
      withDepositFee: boolean;
      packingMethodSubType: any;
    }>
  ) {
    subTypes
      .filter(
        (item, pos) =>
          subTypes.findIndex(
            (it) =>
              it.withDepositFee === item.withDepositFee &&
              it.packingMethodSubType === item.packingMethodSubType
          ) === pos
      )
      .forEach((subType) => {
        const packagingUnits$ = this.store.select(
          ProductCollectionsSelectors.selectPackagingUnitsBySubtype(
            subType.withDepositFee,
            subType.packingMethodSubType
          )
        );
        this.subs.add(
          packagingUnits$.subscribe((packagingUnits) => {
            if (!packagingUnits) {
              this.store.dispatch(
                ProductCollectionsActions.loadPackagingUnitsBySubtype.trigger({
                  withDepositFee: subType.withDepositFee,
                  packingMethodSubType: subType.packingMethodSubType,
                })
              );
            }
          })
        );
      });
  }

  buildCollections(
    collectionGroups: Array<Array<CollectionPackagingUnitHierarchyDto>>
  ) {
    this.collections.clear();
    collectionGroups.forEach((group) => {
      const collectionGroup = this.fb.group({
        collectionLevels: this.fb.array([]),
      });
      group.forEach((collectionLevel) => {
        const collectionLevelForm = this.fb.group({
          id: this.fb.control(collectionLevel.id),
          withDepositFee: this.fb.control(collectionLevel.withDepositFee),
          packingMethodSubType: this.fb.control(
            collectionLevel.packingMethodSubType as any,
            Validators.required
          ),
          refId: this.fb.control(collectionLevel.refId),
          amount: this.fb.control(collectionLevel.amount, [
            Validators.required,
            Validators.min(1),
          ]),
          unit: this.fb.control('PIECES'),
          statementId: this.fb.control(''),
          unique: this.fb.control(false),
        });
        collectionLevelForm.disable();
        (collectionGroup.controls.collectionLevels as FormArray).push(
          collectionLevelForm
        );
      });
      if (this.readOnly) {
        collectionGroup.disable();
      }
      this.collections.push(collectionGroup);
    });
  }

  addHierarchy(groupIndex: number) {
    const collectorLevelForm = this.fb.group({
      id: this.fb.control(''),
      withDepositFee: this.fb.control(false),
      packingMethodSubType: this.fb.control('', Validators.required),
      refId: this.fb.control(''),
      amount: this.fb.control(null, [Validators.required, Validators.min(1)]),
      unit: this.fb.control('PIECES'),
      statementId: this.fb.control(''),
      unique: this.fb.control(false),
    });
    this.collectionLevels(groupIndex).push(collectorLevelForm);
    this.editingGroupIndex = groupIndex;
    this.editingLevelIndex = this.collectionLevels(groupIndex).length - 1;
  }

  onEdit(groupIndex: number, levelIndex: number) {
    this.editingGroupIndex = groupIndex;
    this.editingLevelIndex = levelIndex;
  }

  cancelEditing(isCreationCancelled: boolean, groupIndex: number) {
    if (isCreationCancelled) {
      if (this.collectionLevels(groupIndex).length > 1) {
        this.collectionLevels(groupIndex).removeAt(
          this.collectionLevels(groupIndex).length - 1
        );
        this.editingGroupIndex = null;
        this.editingLevelIndex = null;
      } else {
        if (this.collections.length > 1) {
          this.collections.removeAt(this.collections.length - 1);
          this.editingGroupIndex = null;
          this.editingLevelIndex = null;
        } else {
          this.collectionLevels(0).at(0).reset(this.originalValue);
          this.collections.at(0).enable();
        }
      }
    } else {
      this.editingGroupIndex = null;
      this.editingLevelIndex = null;
    }
  }

  addCollection() {
    const collectionGroup = this.fb.group({
      collectionLevels: this.fb.array([]),
    });
    const collectionLevelForm = this.fb.group({
      id: this.fb.control(''),
      withDepositFee: this.fb.control(false),
      packingMethodSubType: this.fb.control('', Validators.required),
      refId: this.fb.control(''),
      amount: this.fb.control(null, [Validators.required, Validators.min(1)]),
      unit: this.fb.control('PIECES'),
      statementId: this.fb.control(''),
      unique: this.fb.control(false),
    });
    (collectionGroup.controls.collectionLevels as FormArray).push(
      collectionLevelForm
    );
    this.collections.push(collectionGroup);
    this.openedGroupIndexes.push(this.collections.length - 1);
    this.editingGroupIndex = this.collections.length - 1;
    this.editingLevelIndex = 0;
  }

  deleteLast(groupIndex: number) {
    this.store.dispatch(
      ProductCollectionsActions.deleteCollection.trigger({
        packagingUnitId: this.packagingUnitId,
        collectionLevelId: this.collectionLevels(groupIndex).at(
          this.collectionLevels(groupIndex).length - 1
        ).value.id,
      })
    );
  }

  openGroup(opened: any, groupIndex: number) {
    const index = this.openedGroupIndexes.indexOf(groupIndex);
    if (opened && index === -1) {
      this.openedGroupIndexes.push(groupIndex);
    } else if (!opened && index > -1) {
      this.openedGroupIndexes.splice(index, 1);
    }
  }
}
